import type { Dispatch, SetStateAction } from 'react';
import { createContext } from 'react';
import { getInitialVisibleRange } from '../../lib';
import { createResourceGroups } from '../../renderer/groups';
import type { CalendarController } from '../../renderer/controller';
import {
  ABSENCE_LOCK_STATUS,
  type AbsenceEditingLock,
  type AbsenceLockState,
  type SelectedAbsence,
  type TimelineSlot,
  type VisibleRange,
} from '../../types';

/**
 * A controller that does nothing, for the default context.
 *
 * Every operation is a no-op rather than a throw: a component rendered outside
 * the provider should draw nothing, not break, and the calendar it would be
 * talking to does not exist.
 */
const NO_CALENDAR: CalendarController = {
  groups: createResourceGroups(),
  attach: () => {},
  next: () => {},
  prev: () => {},
  today: () => {},
  getDate: () => null,
  select: () => {},
  unselect: () => {},
  changeView: () => {},
  gotoDate: () => {},
  setMonthView: () => {},
  setMonthViewFrom: () => {},
  setDayView: () => {},
  setOption: () => {},
  scrollToResource: () => {},
  revealResource: () => {},
};

const initialLockState: AbsenceLockState = {
  status: ABSENCE_LOCK_STATUS.IDLE,
  lock: null,
  message: null,
};

export type SidebarMode = 'schedule' | 'details' | 'withdraw' | null;

interface TimelineContextValue {
  selectedAbsence: SelectedAbsence | null;
  setSelectedAbsence: Dispatch<SetStateAction<SelectedAbsence | null>>;
  selectedSlot: TimelineSlot | null;
  setSelectedSlot: Dispatch<SetStateAction<TimelineSlot | null>>;
  /**
   * Open a draft, as against adjusting the one that is open.
   *
   * The pane reads what an edit is scoped to when its draft opens and holds it
   * from then on, so this is the call that says "this is a different edit".
   */
  openDraft: (slot: TimelineSlot | null) => void;
  sidebarMode: SidebarMode;
  setSidebarMode: Dispatch<SetStateAction<SidebarMode>>;
  suppressSelectionUntil: number;
  setSuppressSelectionUntil: Dispatch<SetStateAction<number>>;
  selectedUserId: number | null;
  setSelectedUserId: Dispatch<SetStateAction<number | null>>;
  closeEditForm: () => Promise<void>;
  /** Everything the application asks the renderer to do. */
  calendar: CalendarController;
  /** The month the calendar mounts on; stable for the lifetime of the page. */
  initialCalendarDate: Date;
  /**
   * Which view is showing, as its name.
   *
   * The name and nothing else: the renderer reports a whole view object, and
   * holding it here would put a calendar object in application state for the
   * sake of one string.
   */
  viewType: string | null;
  setViewType: (type: string) => void;
  visibleRange: VisibleRange;
  setVisibleRange: Dispatch<SetStateAction<VisibleRange>>;
  absenceLock: AbsenceLockState;
  /** Somebody else holding an edit form open on the user in view, if any.
   * Pushed over `absence.editing`, so it names them while they hold it
   * rather than only in the 409 body of a failed acquire. */
  absenceEditor: AbsenceEditingLock | null;
  isReclaimingLock: boolean;
  setAbsenceLock: Dispatch<SetStateAction<AbsenceLockState>>;
  showLegend: boolean;
  openLegend: () => void;
  closeLegend: () => void;
  showBalanceHistory: boolean;
  openBalanceHistory: () => void;
  closeBalanceHistory: () => void;
  closeAbsenceDetails: () => Promise<void>;
}

export const TimelineContext = createContext<TimelineContextValue>({
  selectedAbsence: null,
  setSelectedAbsence: () => {},
  selectedSlot: null,
  setSelectedSlot: () => {},
  openDraft: () => {},
  sidebarMode: null,
  setSidebarMode: () => {},
  suppressSelectionUntil: 0,
  setSuppressSelectionUntil: () => {},
  selectedUserId: null,
  setSelectedUserId: () => {},
  closeEditForm: async () => {},
  calendar: NO_CALENDAR,
  initialCalendarDate: getInitialVisibleRange().start,
  viewType: null,
  setViewType: () => {},
  visibleRange: getInitialVisibleRange(),
  setVisibleRange: () => {},
  absenceLock: initialLockState,
  absenceEditor: null,
  isReclaimingLock: false,
  setAbsenceLock: () => {},
  showLegend: false,
  openLegend: () => {},
  closeLegend: () => {},
  showBalanceHistory: false,
  openBalanceHistory: () => {},
  closeBalanceHistory: () => {},
  closeAbsenceDetails: async () => {},
});
